import { useEffect, useRef, useState } from "react";
import { formatPrice, isFree, useWakeLock } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import type { RoomChat } from "../chat/useRoomChat";
import type { RecentSale } from "../shows/api";
import {
  formatClock,
  onAirLot,
  recent,
  saleSecondsLeft,
  secondsLeft,
} from "./presenter";
import {
  type PresenterAuction,
  type PresenterLot,
  type PresenterRoom,
  PRESENTER_POLL_MS,
  presenterRoom,
} from "./room";
import styles from "./Presenter.module.scss";

/**
 * Presenter mode: the room as the person on camera needs to see it, drawn over the picture.
 *
 * The picture is not this component's. It is the video already on the show page — the camera
 * preview when this browser is broadcasting, the stream monitor when another device is — lifted
 * to fill the screen by its own component (`Broadcast`, `Monitor`), which renders this inside its
 * frame. That is the design, not a shortcut: `Broadcast` owns the camera and the publish
 * connection, so presenter mode can never be a page of its own — navigating to one would unmount
 * the broadcast. And it is why there is no second video subscription on the encoding device, which
 * `R-VID-9`'s note in the implementation plan says is deliberately avoided.
 *
 * What is drawn is what a viewer's room puts in front of them, sized to be read from across a
 * studio: the comments, the lot on air with its price or its auction, and the audience. Every
 * line of text carries the brand-yellow halo in `Presenter.module.scss`, because a presenter
 * reads this over a moving picture from two metres away, and no single text colour survives every
 * frame.
 *
 * The chat connection is the show page's, shared with the comments card (`useRoomChat`), so
 * opening presenter mode does not open a second one.
 */
export function PresenterOverlay({
  publicId,
  chat,
  sales,
  onExit,
}: {
  publicId: string;
  chat: RoomChat;
  /**
   * The last few sales, newest first — the show page's own read, refreshed on its five-second poll.
   * So a presenter can thank the buyer by name, and say who won the last auction.
   */
  sales: RecentSale[];
  onExit: () => void;
}) {
  const { t } = useTranslation();
  const [room, setRoom] = useState<PresenterRoom | undefined>(undefined);
  /** The server's clock less this machine's, from the last snapshot (`R-VID-1`). */
  const [offset, setOffset] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // The latest `onExit`, for the listeners below, which are attached once.
  const exit = useRef(onExit);
  useEffect(() => {
    exit.current = onExit;
  }, [onExit]);

  // A presenter's laptop left alone for two hours is exactly what a display timeout is for.
  useWakeLock(true);

  useEffect(() => {
    let live = true;
    const pull = () =>
      void presenterRoom(publicId).then(
        (answer) => {
          if (!live) return;
          setRoom(answer);
          const server = Date.parse(answer.serverTime);
          if (Number.isFinite(server)) setOffset(server - Date.now());
        },
        // Silent: the last good snapshot stays on screen, and the next poll is two seconds away.
        () => undefined,
      );
    pull();
    const timer = setInterval(pull, PRESENTER_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [publicId]);

  // The clock is counted down here, from the deadline, rather than re-read — as the room does.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);

  /*
    The browser's own full screen, so the panel's chrome and the browser's both go. Asked for and
    not required: a browser that refuses — or a phone that has no such thing — still gets the
    picture filling the window, which is what the stylesheet does on its own.

    Leaving it by the browser's own means (Esc, a swipe) leaves presenter mode as well, so the two
    can never disagree — a panel left in presenter mode inside a window that is no longer full
    screen would be a mode with no way out that the presenter can see.
  */
  useEffect(() => {
    const root = document.documentElement;
    let entered = false;
    if (!document.fullscreenElement && root.requestFullscreen) {
      root.requestFullscreen().then(
        () => {
          entered = true;
        },
        () => undefined,
      );
    }
    const onChange = () => {
      if (entered && !document.fullscreenElement) exit.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") exit.current();
    };
    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement)
        void document.exitFullscreen().catch(() => undefined);
    };
  }, []);

  const lot = room ? onAirLot(room.lots) : undefined;
  const auction = lot ? room?.auctions[lot.id] : undefined;

  return (
    <div className={styles.overlay}>
      <div className={styles.top}>
        <span className={styles.live}>{t("presenter.live")}</span>
        {room ? (
          <span className={styles.halo}>
            {t("presenter.watching", { count: room.viewers })}
          </span>
        ) : null}
        <button type="button" className={styles.exit} onClick={onExit}>
          {t("presenter.exit")}
        </button>
      </div>

      {/*
        Who just bought or won, and for how much, under the live badge — the four most recent,
        newest first. No product name: the presenter knows what they just sold, and the line is for
        thanking the buyer. Names in the reversed colours the comments use for names.
      */}
      {sales.length > 0 ? (
        <ol className={styles.sales}>
          {sales.map((sale) => (
            <li key={sale.purchaseId} className={styles.sale}>
              <span className={styles.name}>{sale.name ?? t("presenter.someone")}</span>{" "}
              {isFree(sale.amount)
                ? t("presenter.saleFree")
                : t(
                    sale.origin === "win"
                      ? "presenter.saleWon"
                      : "presenter.saleBought",
                    {
                      price: formatPrice(sale.amount),
                    },
                  )}
            </li>
          ))}
        </ol>
      ) : null}

      <div className={styles.bottom}>
        {/*
          Newest at the bottom, nearest the presenter's eye line on the lot, and only the last
          few: a presenter glances, and a wall of text is a screen they stop glancing at.
        */}
        <ol className={styles.comments} aria-live="polite">
          {recent(chat.comments).map((comment) => (
            <li key={comment.id} className={styles.comment}>
              <span className={styles.name}>{comment.name}</span>
              <span className={styles.separator} aria-hidden="true">
                :
              </span>{" "}
              {comment.content}
            </li>
          ))}
        </ol>

        {lot ? <LotCard lot={lot} auction={auction} now={now} offset={offset} /> : null}
      </div>
    </div>
  );
}

function LotCard({
  lot,
  auction,
  now,
  offset,
}: {
  lot: PresenterLot;
  auction: PresenterAuction | undefined;
  now: number;
  offset: number;
}) {
  const { t } = useTranslation();
  const image = lot.images[0];
  const price = (value: string) =>
    isFree(value) ? t("lots.free") : formatPrice(value);

  return (
    <div className={styles.lot}>
      {image ? (
        <img className={styles.image} src={image.url} alt={image.alt ?? ""} />
      ) : null}
      <div className={styles.lotText}>
        <div className={styles.title}>
          {lot.title ?? t("presenter.untitled")}
          {lot.variantTitle ? ` — ${lot.variantTitle}` : ""}
        </div>
        {lot.saleMode === "auction" ? (
          <AuctionLine lot={lot} auction={auction} now={now} offset={offset} />
        ) : (
          <div className={styles.price}>
            {lot.soldOut ? t("presenter.soldOut") : price(lot.price)}
            {/* `R-LOT-34` — a timed sale's clock, as the room shows it. */}
            {!lot.soldOut && lot.closesAt ? (
              <span className={styles.clock}>
                {formatClock(saleSecondsLeft(lot.closesAt, now, offset))}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function AuctionLine({
  lot,
  auction,
  now,
  offset,
}: {
  lot: PresenterLot;
  auction: PresenterAuction | undefined;
  now: number;
  offset: number;
}) {
  const { t } = useTranslation();
  // Featured and not yet opened: no auction state at all, and the starting price is the news.
  if (!auction) {
    return (
      <div className={styles.price}>
        {t("presenter.opensAt", { price: formatPrice(lot.price) })}
      </div>
    );
  }
  if (auction.state === "closed") {
    return (
      <div className={styles.price}>
        {auction.winnerName
          ? t("presenter.won", {
              name: auction.winnerName,
              price: formatPrice(auction.highBid ?? lot.price),
            })
          : t("presenter.unsold")}
      </div>
    );
  }
  const left = secondsLeft(auction, now, offset);
  return (
    <>
      <div className={styles.price}>
        {auction.highBid ? formatPrice(auction.highBid) : formatPrice(lot.price)}
        {left !== undefined ? (
          <span className={styles.clock} data-paused={auction.paused || undefined}>
            {auction.paused ? t("presenter.paused") : formatClock(left)}
          </span>
        ) : null}
      </div>
      <div className={styles.leader}>
        {auction.leaderName
          ? t("presenter.leading", { name: auction.leaderName })
          : t("presenter.noBids")}
        {lot.unitsRemaining !== null && lot.unitsRemaining > 1
          ? ` · ${t("presenter.unitsLeft", { count: lot.unitsRemaining })}`
          : ""}
      </div>
    </>
  );
}
